import { sanitizeForTelemetry } from "./redaction";

export type ApplicationErrorCategory =
  | "configuration"
  | "authentication"
  | "authorization"
  | "validation"
  | "network"
  | "database"
  | "integration"
  | "not_found"
  | "rate_limit"
  | "unexpected";

export type ApplicationErrorSeverity =
  "debug" | "info" | "warning" | "error" | "critical";

export type ApplicationErrorInput = {
  code: string;
  category: ApplicationErrorCategory;
  message: string;
  safeMessage?: string;
  severity?: ApplicationErrorSeverity;
  retryable?: boolean;
  cause?: unknown;
  context?: Record<string, unknown>;
  timestamp?: string;
};

export class ApplicationError extends Error {
  readonly code: string;
  readonly category: ApplicationErrorCategory;
  readonly safeMessage: string;
  readonly severity: ApplicationErrorSeverity;
  readonly retryable: boolean;
  readonly context: Record<string, unknown>;
  readonly timestamp: string;

  constructor(input: ApplicationErrorInput) {
    super(input.message, { cause: input.cause });
    this.name = "ApplicationError";
    this.code = input.code;
    this.category = input.category;
    this.safeMessage =
      input.safeMessage ?? "A safe application error occurred.";
    this.severity = input.severity ?? "error";
    this.retryable = input.retryable ?? false;
    this.context = sanitizeForTelemetry(input.context ?? {}) as Record<
      string,
      unknown
    >;
    this.timestamp = input.timestamp ?? new Date().toISOString();
  }

  toTelemetry() {
    return {
      code: this.code,
      category: this.category,
      message: this.message,
      safeMessage: this.safeMessage,
      severity: this.severity,
      retryable: this.retryable,
      context: this.context,
      timestamp: this.timestamp,
      cause: sanitizeForTelemetry(this.cause),
    };
  }
}

export function createApplicationError(
  input: ApplicationErrorInput,
): ApplicationError {
  return new ApplicationError(input);
}

export function normalizeError(
  error: unknown,
  context: Record<string, unknown> = {},
): ApplicationError {
  if (error instanceof ApplicationError) {
    return error;
  }

  if (error instanceof Error) {
    return new ApplicationError({
      code: "UNEXPECTED_ERROR",
      category: "unexpected",
      message: error.message,
      safeMessage: "An unexpected error occurred.",
      severity: "error",
      cause: error,
      context,
    });
  }

  return new ApplicationError({
    code: "UNKNOWN_THROWN_VALUE",
    category: "unexpected",
    message: "A non-error value was thrown.",
    safeMessage: "An unexpected error occurred.",
    severity: "error",
    cause: sanitizeForTelemetry(error),
    context,
  });
}
